/**
 * Editor-only rule: a `logic_null` (null) value block may be dragged ONLY onto the value input of
 * `set_variable` / `set_aob_variable`, and ONLY when the bound variable is a String or Number. Generic
 * Blockly-provided blocks stay unrestricted; every other custom block rejects null.
 *
 * Implemented on `doDragChecks` (fires for interactive drags only), so deserialization and the headless
 * Forge compiler — which connect programmatically via `doTypeChecks`/`doSafetyChecks` — are untouched.
 * Existing saved functions and compilation are therefore unaffected.
 */
import * as Blockly from 'blockly-tradais';

/** Blockly-provided ("generic") block families — null attaches to these exactly as before. No custom
 *  block in the estate starts with any of these prefixes. */
const GENERIC_BLOCK_RE = /^(logic_|math_|text|lists_|controls_|procedures_|variables_)/;

/** The two custom scalar setters null is allowed on (subject to the value type check below). */
const NULL_ALLOWED_SETTERS = new Set(['set_variable', 'set_aob_variable']);

/**
 * Returns false ONLY when the drag attaches a `logic_null` output to a forbidden input; true otherwise
 * (including every drag that does not involve a `logic_null`). Order-independent in `a`/`b`.
 */
export function nullDropAllowed(a: any, b: any): boolean {
  const aBlk = a && a.getSourceBlock && a.getSourceBlock();
  const bBlk = b && b.getSourceBlock && b.getSourceBlock();
  const aNull = !!aBlk && aBlk.type === 'logic_null';
  const bNull = !!bBlk && bBlk.type === 'logic_null';
  if (!aNull && !bNull) return true;                 // no null involved — not our concern

  const inputConn = aNull ? b : a;                   // the non-null side owns the target input
  const parent = inputConn && inputConn.getSourceBlock && inputConn.getSourceBlock();
  if (!parent || typeof parent.type !== 'string') return true;

  const type: string = parent.type;
  if (GENERIC_BLOCK_RE.test(type)) return true;      // generic Blockly block — unrestricted
  if (NULL_ALLOWED_SETTERS.has(type)) {
    // The setter's VALUE input carries the variable's type check: String→['String','Number'],
    // Number→['Number'], Boolean→['Boolean'], TimeStamp→['TimeStamp'], unconfigured→null.
    const check = inputConn.getCheck && inputConn.getCheck();
    return Array.isArray(check) && (check.includes('String') || check.includes('Number'));
  }
  return false;                                       // any other custom block — reject null
}

const BaseChecker: any = (Blockly as any).ConnectionChecker;

export class ZavoNullConnectionChecker extends BaseChecker {
  // Only interactive drags are gated; everything else falls through to the stock checker.
  doDragChecks(a: any, b: any, distance: number): boolean {
    if (!nullDropAllowed(a, b)) return false;
    return super.doDragChecks(a, b, distance);
  }
}

/** Register the checker under the plugin name the workspace references. Idempotent (allowOverrides). */
export function registerNullConnectionChecker(): void {
  const reg: any = (Blockly as any).registry;
  reg.register(reg.Type.CONNECTION_CHECKER, 'zavoNullChecker', ZavoNullConnectionChecker, true);
}
